import { getCurrentTime, ZonedDateTime } from "@sa-nu/shared-utils/date";

import type { Rule, UserContext } from "./types";

/**
 * 型ガード: user が UserContext かどうかを判定
 */
function isUserContext(user: unknown): user is UserContext {
  return typeof user === "object" && user !== null;
}

/**
 * 特定のUserIDに対して有効化するルール
 * サーバーサイドの場合はこのルールで検出可能だが、ブラウザではUserIDが取得できないので、
 * 別の方法を考える必要がある。（JWTに載せるとか）
 *
 * 実装の参考として残しておく。
 *
 * @alpha
 * @see apps/webapp OpenFeatureProvider
 * @param userIds - 許可するUserIDの配列
 */
export function enableUserIdsRule(userIds: readonly string[]): Rule {
  return (ctx) => {
    const user = ctx.user;
    if (!isUserContext(user) || !user.userId) return false;
    return userIds.includes(user.userId);
  };
}

/**
 * 指定日時以降に有効化するルール（時限式）
 *
 * @param releaseDate - リリース日時
 */
export function enableReleasedAtRule(releaseDate: ZonedDateTime): Rule {
  return () => {
    const now = getCurrentTime();
    return ZonedDateTime.compare(now, releaseDate) >= 0;
  };
}

/**
 * 指定した APP_ENV で有効化するルール。
 * 評価コンテキストの `appEnv` を見る。
 */
export function enableAppEnvsRule(appEnvs: readonly string[]): Rule {
  return (ctx) => {
    const appEnv = ctx["appEnv"];
    return typeof appEnv === "string" && appEnvs.includes(appEnv);
  };
}

/**
 * メールアドレスのドメインで有効化するルール。
 * 評価コンテキストの `email` を見る。確認済みのメールだけを渡すのは呼び出し側の責務。
 */
export function enableEmailDomainsRule(domains: readonly string[]): Rule {
  return (ctx) => {
    const email = ctx["email"];
    return (
      typeof email === "string" &&
      domains.some((domain) => email.endsWith(`@${domain}`))
    );
  };
}
